/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import cytoscape, { type Core, type ElementDefinition, type StylesheetStyle } from 'cytoscape';
import dagre from 'cytoscape-dagre';
import { getNodeColors, getEdgeColor, buildNodeIcons } from '@cade/graph-logic';
import { runLayout } from '@cade/graph-logic/renderer';

// Register dagre layout extension
cytoscape.use(dagre);

export type LayoutType = 'attack-path' | 'tree' | 'force' | 'layered';

export interface CytoscapeRendererHandle {
  centerOnNode: (nodeId: string) => void;
  highlightNode: (nodeId: string | null) => void;
  focusOnNode: (nodeId: string) => void;
  clearFocus: () => void;
  resize: () => void;
}

interface Props {
  elements: ElementDefinition[];
  layout: LayoutType;
  onNodeClick?: (nodeId: string, data: Record<string, unknown>) => void;
  highlightedNodeId?: string | null;
  onFocusChange?: (isFocused: boolean) => void;
  criticalPath?: string[];
  showEdgeLabels?: boolean;
  onNodeHover?: (nodeLabel: string | null, aliases?: string[]) => void;
  orientation?: 'vertical' | 'horizontal';
}

// Orientation configs — vertical (TB) is default, horizontal (LR) preserved for Phase 2 toggle
export const DAGRE_VERTICAL = {
  name: 'dagre',
  rankDir: 'TB',
  // Keep node separation wide enough that same-rank labels don't collide (matches
  // the shared headless layoutConfigs used by the default ObsidianGraph renderer).
  nodeSep: 170,
  rankSep: 120,
  edgeSep: 20,
  animate: true,
  animationDuration: 400,
  padding: 50,
  fit: true,
};

export const DAGRE_HORIZONTAL = {
  name: 'dagre',
  rankDir: 'LR',
  nodeSep: 170,
  rankSep: 140,
  edgeSep: 20,
  animate: true,
  animationDuration: 400,
  padding: 50,
  fit: true,
};

/** Returns edge style config for a given layout and orientation. */
export function getEdgeStyleForLayout(layout: LayoutType, orientation: 'vertical' | 'horizontal') {
  if (layout === 'attack-path') {
    return {
      'curve-style': 'taxi' as const,
      'taxi-direction': orientation === 'vertical' ? 'downward' as const : 'rightward' as const,
      'taxi-turn': 30,
      'target-distance-from-node': 2,
    };
  }
  return {
    'curve-style': 'unbundled-bezier' as const,
    'control-point-distances': [45],
    'control-point-weights': [0.5],
  };
}

// WI-9B: Attack Path (dagre), Tree (breadthfirst), Force (cose)
function getLayoutConfigs(orientation: 'vertical' | 'horizontal'): Record<LayoutType, any> {
  return {
    'attack-path': orientation === 'vertical' ? DAGRE_VERTICAL : DAGRE_HORIZONTAL,
    tree: {
      name: 'breadthfirst',
      directed: true,
      spacingFactor: 2.2,
      animate: true,
      animationDuration: 400,
      padding: 50,
    },
    force: {
      name: 'cose',
      animate: true,
      animationDuration: 400,
      nodeRepulsion: () => 30000,
      idealEdgeLength: () => 180,
      gravity: 0.05,
      padding: 50,
      nodeOverlap: 20,
      numIter: 1000,
      initialTemp: 300,
    },
    // Layered uses a custom solver in @cade/graph-logic::runLayout (rank by
    // 6-layer ontology). ObsidianGraph reads the headless positions itself;
    // CytoscapeRenderer (the fallback when `useObsidianGraph = false`) reads
    // them via a `positions` callback so `preset` has actual coordinates to
    // place. Without this, `preset` lands every node at the origin and the
    // fallback codepath is broken.
    layered: {
      name: 'preset',
      animate: false,
      fit: true,
      padding: 50,
    } as any,
  };
}

/** Fit graph to viewport, capping zoom for small graphs so nodes don't appear enormous. */
function fitGraph(cy: Core) {
  cy.fit(undefined, 20);
  // Scale max auto-zoom by node count: few nodes = lower cap
  const nodeCount = cy.nodes().length;
  const maxZoom = nodeCount <= 3 ? 0.9 : nodeCount <= 8 ? 1.2 : 2.0;
  if (cy.zoom() > maxZoom) {
    cy.zoom(maxZoom);
    cy.center();
  }
}

/**
 * Read a CSS custom property off :root at build time. Cytoscape cannot resolve
 * `var(--x)`, so any palette/theme token we need must be resolved here. Returns
 * the trimmed value, or the provided fallback when unavailable (e.g. SSR).
 */
function cssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function buildStylesheet(isDark: boolean): StylesheetStyle[] {
  const textColor = isDark ? '#e4e4e7' : '#27272a';
  const textOutline = isDark ? '#18181b' : '#ffffff';
  const edgeLabelColor = isDark ? '#a1a1aa' : '#71717a';
  const selectedBorder = isDark ? '#f4f4f5' : '#18181b';
  const icons = buildNodeIcons(isDark ? '#FFFFFF' : '#3f3f46');
  // Resolved theme tokens — Cytoscape can't read CSS vars directly.
  const mutedColor = cssVar('--color-text-muted', isDark ? '#a1a1aa' : '#71717a');

  const styles: StylesheetStyle[] = [
    // ── Base node — category-colored fill with white icon ──
    {
      selector: 'node',
      style: {
        'background-color': function(ele: any) {
          return getNodeColors(ele.data('type') ?? 'unknown', isDark).fill;
        },
        'background-image': icons.default,
        'background-width': '50%',
        'background-height': '50%',
        'background-clip': 'none',
        'background-image-containment': 'over',
        'label': function(ele: any) {
          const alertCount = ele.data('alertCount');
          let label: string = ele.data('label') ?? '';
          if (label.length > 20) label = label.slice(0, 18) + '\u2026';
          // Collar fallback: entity TYPE (uppercased) above the ID/name.
          const type: string = ele.data('type') ?? '';
          const typeLine = type ? `${String(type).toUpperCase()}\n` : '';
          if (alertCount && alertCount > 0) {
            return `${typeLine}${label}\n\u26A0 ${alertCount}`;
          }
          return `${typeLine}${label}`;
        },
        'color': textColor,
        'text-valign': 'bottom',
        'text-halign': 'center',
        'font-size': '11px',
        'font-family': 'DM Sans, system-ui, sans-serif',
        'font-weight': 500,
        'text-margin-y': 8,
        'text-outline-color': textOutline,
        'text-outline-width': 2,
        'text-outline-opacity': 0.8,
        'text-max-width': '100px',
        'text-wrap': 'wrap',
        'shape': 'ellipse',
        'width': 40,
        'height': 40,
        'border-width': 2,
        'border-color': function(ele: any) {
          return getNodeColors(ele.data('type') ?? 'unknown', isDark).stroke;
        },
        'border-opacity': 1,
        'background-opacity': 1,
        'overlay-padding': 6,
        'transition-property': 'border-color, border-width, width, height',
        'transition-duration': 200,
      } as any,
    },
    // ── Severity border colors (override category stroke) + glow for critical/high ──
    { selector: 'node[severity="critical"]', style: { 'border-color': '#dc2626', 'underlay-color': '#dc2626', 'underlay-padding': 4, 'underlay-opacity': 0.15, 'underlay-shape': 'ellipse' } as any },
    { selector: 'node[severity="high"]',     style: { 'border-color': '#ea580c', 'underlay-color': '#ea580c', 'underlay-padding': 4, 'underlay-opacity': 0.15, 'underlay-shape': 'ellipse' } as any },
    { selector: 'node[severity="medium"]',   style: { 'border-color': '#ca8a04' } },
    { selector: 'node[severity="low"]',      style: { 'border-color': '#2563eb' } },
    { selector: 'node[severity="ok"]',       style: { 'border-color': '#16a34a' } },
    // ── DNS validation_state risk overlay — applies to `domain` nodes only.
    // Spec: docs/spec/topics/connectors/dns-domain-entity-extraction.md §1.2.
    // The DNS baseline attaches `validation_state` to every domain it
    // promotes; the analyst-facing posture meaning of each state is the same
    // shape as severity above:
    //   dangling-takeover-candidate  → red glow (high-priority risk)
    //   unresolved-but-corroborated  → amber border (low-confidence)
    //   resolves-no-service          → soft amber border
    //   live-serving                 → green border (healthy)
    { selector: 'node[type="domain"][validation_state="dangling-takeover-candidate"]', style: { 'border-color': '#dc2626', 'underlay-color': '#dc2626', 'underlay-padding': 4, 'underlay-opacity': 0.2, 'underlay-shape': 'ellipse' } as any },
    { selector: 'node[type="domain"][validation_state="unresolved-but-corroborated"]', style: { 'border-color': '#ca8a04', 'border-style': 'dashed' } as any },
    { selector: 'node[type="domain"][validation_state="resolves-no-service"]',         style: { 'border-color': '#d97706' } as any },
    { selector: 'node[type="domain"][validation_state="live-serving"]',                style: { 'border-color': '#16a34a' } as any },
    // ── Node type icons ──
    { selector: 'node[type="host"]',         style: { 'background-image': icons.host } as any },
    { selector: 'node[type="user"]',         style: { 'background-image': icons.user } as any },
    { selector: 'node[type="alert"]',        style: { 'background-image': icons.alert } as any },
    { selector: 'node[type="ip"]',           style: { 'background-image': icons.ip } as any },
    { selector: 'node[type="process"]',      style: { 'background-image': icons.process } as any },
    { selector: 'node[type="file"]',         style: { 'background-image': icons.file } as any },
    { selector: 'node[type="domain"]',       style: { 'background-image': icons.atSign } as any },
    { selector: 'node[type="hash"]',         style: { 'background-image': icons.hash } as any },
    // Identity
    { selector: 'node[type="iam_role"]',     style: { 'background-image': icons.shieldCheck } as any },
    { selector: 'node[type="group"]',        style: { 'background-image': icons.users } as any },
    { selector: 'node[type="certificate"]',  style: { 'background-image': icons.lock } as any },
    { selector: 'node[type="secret"]',       style: { 'background-image': icons.keyRound } as any },
    // Compute
    { selector: 'node[type="ec2"]',          style: { 'background-image': icons.server } as any },
    { selector: 'node[type="lambda"]',       style: { 'background-image': icons.zap } as any },
    { selector: 'node[type="container"]',    style: { 'background-image': icons.box } as any },
    { selector: 'node[type="k8s_pod"]',      style: { 'background-image': icons.container } as any },
    { selector: 'node[type="k8s_node"]',     style: { 'background-image': icons.container } as any },
    { selector: 'node[type="eks_cluster"]',  style: { 'background-image': icons.network } as any },
    { selector: 'node[type="aks_cluster"]',  style: { 'background-image': icons.network } as any },
    { selector: 'node[type="gke_cluster"]',  style: { 'background-image': icons.network } as any },
    { selector: 'node[type="auto_scaling_group"]', style: { 'background-image': icons.moveDiagonal } as any },
    // Network
    { selector: 'node[type="url"]',          style: { 'background-image': icons.link } as any },
    { selector: 'node[type="network_zone"]', style: { 'background-image': icons.layers } as any },
    { selector: 'node[type="asn"]',          style: { 'background-image': icons.radioTower } as any },
    { selector: 'node[type="security_group"]', style: { 'background-image': icons.shield } as any },
    // Data
    { selector: 'node[type="s3_bucket"]',    style: { 'background-image': icons.archive } as any },
    { selector: 'node[type="storage_bucket"]', style: { 'background-image': icons.archive } as any },
    { selector: 'node[type="database"]',     style: { 'background-image': icons.database } as any },
    { selector: 'node[type="rds"]',          style: { 'background-image': icons.database } as any },
    { selector: 'node[type="dynamodb"]',     style: { 'background-image': icons.database } as any },
    { selector: 'node[type="data_finding"]', style: { 'background-image': icons.scanEye } as any },
    // Findings
    { selector: 'node[type="vulnerability"]', style: { 'background-image': icons.shieldAlert } as any },
    { selector: 'node[type="incident"]',     style: { 'background-image': icons.folderOpen } as any },
    { selector: 'node[type="threat_actor"]', style: { 'background-image': icons.skull } as any },
    { selector: 'node[type="campaign"]',     style: { 'background-image': icons.flag } as any },
    // Application
    { selector: 'node[type="application"]',  style: { 'background-image': icons.appWindow } as any },
    { selector: 'node[type="email"]',        style: { 'background-image': icons.mail } as any },
    // Context
    { selector: 'node[type="organization"]', style: { 'background-image': icons.building2 } as any },
    { selector: 'node[type="geolocation"]',  style: { 'background-image': icons.mapPin } as any },
    // Framework
    { selector: 'node[type="mitre_technique"]', style: { 'background-image': icons.crosshair } as any },
    { selector: 'node[type="policy"]',       style: { 'background-image': icons.scroll } as any },
    { selector: 'node[type="cloud_resource"]', style: { 'background-image': icons.cloud } as any },
    // ── Internet canonical node — larger, dashed border, bigger icon ──
    { selector: 'node[type="internet"]', style: { 'width': 52, 'height': 52, 'border-style': 'dashed', 'border-width': 2, 'background-image': icons.globe2, 'background-width': '60%', 'background-height': '60%' } as any },
    // ── Ontological class → shape ──
    // Activity (event/moment, not a thing): alert / incident → octagon.
    { selector: 'node[type="alert"], node[type="incident"]', style: { 'shape': 'octagon', 'width': 44, 'height': 44 } as any },
    // Property nodes (location of an entity, MITRE technique of an activity):
    // muted, dashed, smaller, tag-like (Cytoscape has no 'round-tag').
    { selector: 'node[type="geolocation"], node[type="mitre_technique"]', style: { 'shape': 'round-rectangle', 'background-opacity': 0.6, 'border-opacity': 0.6, 'border-style': 'dashed', 'width': 30, 'height': 30 } as any },
    // ── Hover & selected ──
    { selector: 'node:active', style: { 'overlay-opacity': 0.08 } },
    { selector: 'node:selected', style: { 'border-color': selectedBorder, 'border-width': 3, 'width': 44, 'height': 44 } as any },
    // ── Base edge — category-colored gradient, hidden labels by default ──
    {
      selector: 'edge',
      style: {
        'width': 1.2,
        'line-color': function(ele: any) {
          return getEdgeColor(ele.source().data('type') ?? 'unknown', isDark);
        },
        'curve-style': 'unbundled-bezier',
        'control-point-distances': [45],
        'control-point-weights': [0.5],
        'target-arrow-shape': 'triangle',
        'target-arrow-color': function(ele: any) {
          return getEdgeColor(ele.source().data('type') ?? 'unknown', isDark);
        },
        'arrow-scale': 1.0,
        'source-arrow-shape': 'none',
        'label': '',
        'font-size': '8px',
        'color': edgeLabelColor,
        'text-rotation': 'autorotate',
        'font-family': 'DM Sans, system-ui, sans-serif',
        'text-outline-color': textOutline,
        'text-outline-width': 2,
        'text-outline-opacity': 0.6,
        'line-opacity': 0.5,
        'transition-property': 'line-color, width, line-opacity',
        'transition-duration': 200,
      } as any,
    },
    { selector: 'edge[style="dashed"]', style: { 'line-style': 'dashed', 'line-dash-pattern': [5, 4] as any } },
    // ── Property edges — entity→location / activity→MITRE. Dotted, muted, no arrowhead. ──
    // `isProperty` boolean is set in @cade/graph-logic graphEventToElements.
    { selector: 'edge[?isProperty]', style: { 'line-style': 'dotted', 'line-color': mutedColor, 'target-arrow-shape': 'none', 'opacity': 0.5, 'width': 1 } as any },
    // ── Edge label visibility ──
    { selector: 'edge.show-label', style: { 'label': 'data(label)' } as any },
    { selector: 'edge.labels-visible', style: { 'label': 'data(label)' } as any },
    // ── Highlighted edges (connected to selected node) ──
    { selector: 'node:selected ~ edge, edge:selected', style: { 'width': 2, 'line-opacity': 0.8 } },
    // ── Cross-reference highlight (entity chip hover / graph node hover) ──
    {
      selector: 'node.cross-ref-highlight',
      style: {
        'border-color': function(ele: any) {
          return getEdgeColor(ele.data('type') ?? 'unknown', isDark);
        },
        'border-width': 3,
        'border-opacity': 1,
        'width': 54,
        'height': 54,
        'underlay-color': function(ele: any) {
          return getEdgeColor(ele.data('type') ?? 'unknown', isDark);
        },
        'underlay-padding': 6,
        'underlay-opacity': 0.3,
        'underlay-shape': 'ellipse',
        'z-index': 20,
        'font-weight': 'bold',
      } as any,
    },
    { selector: '.cross-ref-neighbor', style: { 'opacity': 1, 'border-width': 3, 'z-index': 15 } as any },
    { selector: '.cross-ref-dimmed', style: { 'opacity': 0.4, 'text-opacity': 0.4 } as any },
    // ── Focus mode ──
    { selector: '.dimmed', style: { 'opacity': 0.12, 'text-opacity': 0, 'transition-property': 'opacity, text-opacity', 'transition-duration': 300 } as any },
    { selector: '.focused', style: { 'opacity': 1, 'text-opacity': 1, 'transition-property': 'opacity, text-opacity', 'transition-duration': 300 } as any },
    { selector: '.focus-target', style: { 'border-width': 4, 'border-color': selectedBorder, 'width': 52, 'height': 52, 'font-weight': 'bold', 'z-index': 10, 'transition-property': 'border-width, width, height', 'transition-duration': 300 } as any },
    { selector: 'edge.focused', style: { 'width': 2.5, 'line-opacity': 1, 'transition-property': 'width, line-opacity', 'transition-duration': 300 } as any },
    // ── WI-9D: Critical path highlighting (labels always visible) ──
    {
      selector: 'edge.critical-path',
      style: {
        'line-color': '#ef4444',
        'target-arrow-color': '#ef4444',
        'width': 2.5,
        'line-opacity': 1,
        'arrow-scale': 1.2,
        'z-index': 10,
        'line-style': 'solid',
        'label': 'data(label)',
      } as any,
    },
    {
      selector: 'node.critical-path-node',
      style: {
        'width': 32,
        'height': 32,
        'underlay-color': '#ef4444',
        'underlay-padding': 4,
        'underlay-opacity': 0.12,
        'underlay-shape': 'ellipse',
        'z-index': 10,
      } as any,
    },
    // Dim non-critical elements when path is active
    { selector: '.path-dimmed', style: { 'opacity': 0.3, 'text-opacity': 0.3 } as any },
  ];

  return styles;
}

export const CytoscapeRenderer = forwardRef<CytoscapeRendererHandle, Props>(function CytoscapeRenderer({ elements, layout, onNodeClick, highlightedNodeId, onFocusChange, criticalPath, showEdgeLabels, onNodeHover, orientation = 'vertical' }, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  const suppressResizeFitRef = useRef(true);
  // Track previous element IDs to detect wholesale replacement vs incremental update.
  // Only incremental additions (streaming) should animate; full replacements (navigation) should not.
  const prevElementIdsRef = useRef<Set<string>>(new Set());
  const layoutMountedRef = useRef(false);
  const onNodeHoverRef = useRef(onNodeHover);
  onNodeHoverRef.current = onNodeHover;

  useImperativeHandle(ref, () => ({
    centerOnNode(nodeId: string) {
      const cy = cyRef.current;
      if (!cy) return;
      const node = cy.getElementById(nodeId);
      if (node.length > 0) {
        cy.animate({ center: { eles: node }, zoom: cy.zoom() }, { duration: 300 });
      }
    },
    highlightNode(nodeId: string | null) {
      const cy = cyRef.current;
      if (!cy) return;
      // Clear previous highlight state
      cy.nodes().removeClass('cross-ref-highlight cross-ref-neighbor cross-ref-dimmed');
      const baseOpacity = cy.edges().length >= 50 ? 0.25 : 0.5;
      cy.edges().not('.critical-path').style({ 'line-opacity': baseOpacity, 'width': 1.2, 'target-arrow-shape': 'triangle' });
      if (nodeId) {
        const node = cy.getElementById(nodeId);
        if (node.length > 0) {
          // Full highlight: glow, neighbors, edge dimming — same as graph hover
          const neighbors = node.neighborhood().nodes();
          node.addClass('cross-ref-highlight');
          neighbors.addClass('cross-ref-neighbor');
          cy.nodes().not(node).not(neighbors).addClass('cross-ref-dimmed');
          const connected = node.connectedEdges();
          connected.style({ 'line-opacity': 0.9, 'width': 2, 'target-arrow-shape': 'triangle' });
          cy.edges().not(connected).not('.critical-path').style({ 'line-opacity': 0.15, 'target-arrow-shape': 'none' });
        }
      }
    },
    focusOnNode(nodeId: string) {
      const cy = cyRef.current;
      if (!cy) return;
      const node = cy.getElementById(nodeId);
      if (node.length === 0) return;
      const neighborhood = node.closedNeighborhood();
      cy.elements().not(neighborhood).addClass('dimmed').removeClass('focused');
      neighborhood.addClass('focused').removeClass('dimmed');
      node.addClass('focus-target');
      cy.animate({ fit: { eles: neighborhood, padding: 60 } }, { duration: 400 });
      onFocusChange?.(true);
    },
    clearFocus() {
      const cy = cyRef.current;
      if (!cy) return;
      cy.elements().removeClass('dimmed focused focus-target');
      onFocusChange?.(false);
    },
    resize() {
      const cy = cyRef.current;
      if (!cy) return;
      cy.resize();
      fitGraph(cy);
    },
  }));

  // Apply/clear cross-reference highlight when highlightedNodeId changes
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    // Clear previous state
    cy.nodes().removeClass('cross-ref-highlight cross-ref-neighbor cross-ref-dimmed');
    const baseOpacity = cy.edges().length >= 50 ? 0.25 : 0.5;
    cy.edges().not('.critical-path').style({ 'line-opacity': baseOpacity, 'width': 1.2, 'target-arrow-shape': 'triangle' });
    if (highlightedNodeId) {
      const node = cy.getElementById(highlightedNodeId);
      if (node.length > 0) {
        const neighbors = node.neighborhood().nodes();
        node.addClass('cross-ref-highlight');
        neighbors.addClass('cross-ref-neighbor');
        cy.nodes().not(node).not(neighbors).addClass('cross-ref-dimmed');
        const connected = node.connectedEdges();
        connected.style({ 'line-opacity': 0.9, 'width': 2, 'target-arrow-shape': 'triangle' });
        cy.edges().not(connected).not('.critical-path').style({ 'line-opacity': 0.15, 'target-arrow-shape': 'none' });
      }
    }
  }, [highlightedNodeId]);

  // WI-9D: Apply/remove critical path highlighting
  // criticalPath can be:
  //   - Edge IDs: ["src-type-tgt", ...] matching Cytoscape edge IDs
  //   - Node ID sequence: ["nodeA", "nodeB", "nodeC"] — highlight edges between consecutive nodes
  //   - Mixed short IDs: ["e1", "e2"] — matched by edge ID or fuzzy
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    cy.elements().removeClass('critical-path critical-path-node path-dimmed');

    if (!criticalPath || criticalPath.length === 0) return;

    let matched = 0;
    const pathEdgeIds = new Set(criticalPath);

    // Strategy 1: Try matching as edge IDs (exact or source-label-target combo)
    cy.edges().forEach((edge) => {
      const eid = edge.id();
      const src = edge.data('source') as string;
      const tgt = edge.data('target') as string;
      const label = (edge.data('label') as string ?? '').replace(/\s/g, '_');

      if (pathEdgeIds.has(eid) || pathEdgeIds.has(`${src}-${label}-${tgt}`)) {
        edge.addClass('critical-path');
        edge.source().addClass('critical-path-node');
        edge.target().addClass('critical-path-node');
        matched++;
      }
    });

    // Strategy 2: Treat criticalPath as an ordered list of NODE IDs/labels
    // and highlight edges between consecutive nodes in the sequence
    if (matched === 0) {
      // Build lookup maps for resolving criticalPath entries to Cytoscape node IDs
      const idSet = new Set<string>();
      const labelToId = new Map<string, string>();
      // Also index by ID with type prefix stripped (e.g. "host-ws042" → "ws042")
      const strippedIdToId = new Map<string, string>();
      cy.nodes().forEach((n) => {
        const nid = n.id();
        idSet.add(nid);
        const lbl = (n.data('label') as string ?? '').toLowerCase();
        if (lbl) labelToId.set(lbl, nid);
        // Strip common type prefixes for fuzzy matching
        const stripped = nid.replace(/^(host|ip|user|alert|process|file|domain|hash|app|role|sa|svc|ec2|lambda|k8s|asn|internet|org|geo|vuln|threat|campaign|cert|secret|sg|s3|rds|db|mitre|policy|email|container)-/, '');
        if (stripped !== nid) strippedIdToId.set(stripped.toLowerCase(), nid);
      });

      // Resolve each criticalPath entry to an actual Cytoscape node ID
      const resolvedIds: string[] = [];
      for (const entry of criticalPath) {
        // 1. Exact ID match
        if (idSet.has(entry)) {
          resolvedIds.push(entry);
          continue;
        }
        // 2. Exact label match (case-insensitive)
        const byLabel = labelToId.get(entry.toLowerCase());
        if (byLabel) {
          resolvedIds.push(byLabel);
          continue;
        }
        // 3. Stripped prefix match (entry without type prefix matches a node's stripped ID)
        const entryStripped = entry.replace(/^(host|ip|user|alert|process|file|domain|hash|app|role|sa|svc|ec2|lambda|k8s|asn|internet|org|geo|vuln|threat|campaign|cert|secret|sg|s3|rds|db|mitre|policy|email|container)-/, '').toLowerCase();
        const byStripped = strippedIdToId.get(entryStripped);
        if (byStripped) {
          resolvedIds.push(byStripped);
          continue;
        }
        // 4. Substring/contains match — check both directions for ID, label, and stripped values
        let found: string | null = null;
        let bestScore = 0;
        const eLower = entry.toLowerCase();
        cy.nodes().forEach((n) => {
          const nid = n.id();
          const lbl = (n.data('label') as string ?? '').toLowerCase();
          const nidLower = nid.toLowerCase();
          // Check all forms of containment
          const candidates = [nidLower, lbl];
          for (const c of candidates) {
            if (!c) continue;
            // Bidirectional contains
            if (c.includes(eLower) || eLower.includes(c)) {
              const score = Math.min(c.length, eLower.length);
              if (score > bestScore) { bestScore = score; found = nid; }
            }
            // Also check if significant tokens overlap (split on non-alphanumeric)
            const entryTokens = eLower.split(/[^a-z0-9]+/).filter(t => t.length >= 3);
            const cTokens = c.split(/[^a-z0-9]+/).filter(t => t.length >= 3);
            const overlap = entryTokens.filter(t => cTokens.some(ct => ct.includes(t) || t.includes(ct)));
            if (overlap.length > 0 && overlap.length >= Math.min(entryTokens.length, cTokens.length)) {
              const score = overlap.join('').length;
              if (score > bestScore) { bestScore = score; found = nid; }
            }
          }
        });
        if (found) resolvedIds.push(found);
      }

      if (resolvedIds.length >= 2) {
        // First, mark all path nodes
        const pathNodeSet = new Set(resolvedIds);
        for (const nid of resolvedIds) {
          const node = cy.getElementById(nid);
          if (node.length > 0) node.addClass('critical-path-node');
        }

        // Then highlight edges between consecutive path nodes
        for (let i = 0; i < resolvedIds.length - 1; i++) {
          const src = resolvedIds[i]!;
          const tgt = resolvedIds[i + 1]!;
          cy.edges().forEach((edge) => {
            const eSrc = edge.data('source') as string;
            const eTgt = edge.data('target') as string;
            if ((eSrc === src && eTgt === tgt) || (eSrc === tgt && eTgt === src)) {
              edge.addClass('critical-path');
              matched++;
            }
          });
        }

        // If some consecutive pairs had no direct edge, count node matches
        // so we still dim non-path elements
        if (matched === 0 && pathNodeSet.size >= 2) matched = 1;
      }
    }

    if (matched > 0) {
      cy.elements().not('.critical-path, .critical-path-node').addClass('path-dimmed');
    }
  }, [criticalPath, elements]);

  // Detect dark mode reactively via data-theme attribute
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
  );

  useEffect(() => {
    const el = document.documentElement;
    const update = () => setIsDark(el.getAttribute('data-theme') === 'dark');
    const observer = new MutationObserver(update);
    observer.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Initialize Cytoscape once
  useEffect(() => {
    if (!containerRef.current) return;

    const cy = cytoscape({
      container: containerRef.current,
      elements: [],
      style: buildStylesheet(isDark),
      layout: { name: 'preset' },
      minZoom: 0.3,
      maxZoom: 3,
      wheelSensitivity: 1.5,
    });

    cyRef.current = cy;

    if (onNodeClick) {
      cy.on('tap', 'node', (evt) => {
        const node = evt.target;
        onNodeClick(node.id(), node.data());
      });
    }

    cy.on('tap', (evt) => {
      if (evt.target === cy) {
        cy.elements().removeClass('dimmed focused focus-target');
        onFocusChange?.(false);
      }
    });

    // Edge hover — show label on individual edge
    cy.on('mouseover', 'edge', (evt) => {
      evt.target.addClass('show-label');
    });
    cy.on('mouseout', 'edge', (evt) => {
      evt.target.removeClass('show-label');
    });

    // Node hover — highlight node + connected edges + dispatch to chat
    cy.on('mouseover', 'node', (evt) => {
      const node = evt.target;
      // Highlight the node with glow, neighbors slightly, dim the rest
      const neighbors = node.neighborhood().nodes();
      node.addClass('cross-ref-highlight');
      neighbors.addClass('cross-ref-neighbor');
      cy.nodes().not(node).not(neighbors).addClass('cross-ref-dimmed');
      // Highlight connected edges
      const connected = node.connectedEdges();
      connected.style({ 'line-opacity': 0.9, 'width': 2, 'target-arrow-shape': 'triangle' });
      cy.edges().not(connected).not('.critical-path').style({ 'line-opacity': 0.15, 'target-arrow-shape': 'none' });
      // Highlight matching entity chips in chat
      onNodeHoverRef.current?.(node.data('label'), node.data('aliases'));
    });
    cy.on('mouseout', 'node', () => {
      cy.nodes().removeClass('cross-ref-highlight cross-ref-neighbor cross-ref-dimmed');
      const baseOpacity = cy.edges().length >= 50 ? 0.25 : 0.5;
      cy.edges().not('.critical-path').style({ 'line-opacity': baseOpacity, 'width': 1.2, 'target-arrow-shape': 'triangle' });
      onNodeHoverRef.current?.(null);
    });

    // Suppress fitGraph from ResizeObserver until after the first layout completes.
    // Without this, mount → layout → fit → ResizeObserver → fit creates a visible zoom bounce.
    suppressResizeFitRef.current = true;
    let resizeTimer: ReturnType<typeof setTimeout>;
    const resizeObserver = new ResizeObserver(() => {
      cy.resize();
      if (suppressResizeFitRef.current) return;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        fitGraph(cy);
      }, 150);
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(resizeTimer);
      cy.destroy();
      cyRef.current = null;
      // Reset refs so a remount (e.g. React StrictMode double-mount) doesn't
      // incorrectly bail thinking elements are already loaded
      prevElementIdsRef.current = new Set();
      suppressResizeFitRef.current = true;
      layoutMountedRef.current = false;
    };
  }, []); // mount-only: intentionally no deps

  // Re-apply stylesheet when theme changes (without destroying the instance)
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.style(buildStylesheet(isDark) as any);
    // Re-apply layout-adaptive curve style (stylesheet reset clears it)
    const edgeStyle = getEdgeStyleForLayout(layout, orientation);
    cy.style().selector('edge').style(edgeStyle).update();
  }, [isDark, layout, orientation]);

  // Toggle edge labels via toolbar
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    if (showEdgeLabels) {
      cy.edges().addClass('labels-visible');
    } else {
      cy.edges().removeClass('labels-visible');
    }
  }, [showEdgeLabels]);

  // Update elements
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    const newIds = new Set(elements.map((el) => el.data.id as string));
    const prevIds = prevElementIdsRef.current;

    // Early bail: if element IDs haven't changed, skip entirely (avoids redundant runs
    // from React Query refetches that create new array references for the same data)
    if (newIds.size === prevIds.size && newIds.size > 0) {
      let same = true;
      for (const id of newIds) { if (!prevIds.has(id)) { same = false; break; } }
      if (same) return;
    }

    const existingIds = new Set(cy.elements().map((el) => el.id()));

    // Remove elements no longer present
    const removedCount = cy.elements().filter((el) => !newIds.has(el.id())).length;
    cy.elements().forEach((el) => {
      if (!newIds.has(el.id())) el.remove();
    });

    const toAdd = elements.filter((el) => !existingIds.has(el.data.id as string));

    if (toAdd.length > 0) {
      const nodes = toAdd.filter((el) => !el.data.source);
      const edges = toAdd.filter((el) => !!el.data.source);

      if (nodes.length > 0) cy.add(nodes);

      const nodeIds = new Set(cy.nodes().map((n) => n.id()));
      for (const edge of edges) {
        const src = edge.data.source as string;
        const tgt = edge.data.target as string;
        if (nodeIds.has(src) && nodeIds.has(tgt)) {
          try { cy.add(edge); } catch { /* skip */ }
        }
      }
    }

    // Update the tracking ref
    prevElementIdsRef.current = newIds;

    if (toAdd.length > 0 && cy.elements().length > 0) {
      // Determine if this is an incremental update (streaming new nodes into existing graph)
      // vs a wholesale replacement (navigation, initial load, or conversation switch).
      // A wholesale replacement is when: the graph was empty, OR most existing elements were removed.
      const isIncremental = prevIds.size > 0 && removedCount === 0 && existingIds.size > 0;
      const shouldAnimate = isIncremental;

      const overrides = shouldAnimate ? {} : { animate: false, fit: false };
      // For the `layered` preset, pre-solve the headless positions and
      // attach them as a `positions` callback so Cytoscape's `preset` layout
      // actually has coordinates to place. Without this the fallback
      // renderer collapses every node to (0, 0) under the layered layout.
      const layeredPositions = layout === 'layered'
        ? runLayout(elements as never, 'layered', orientation)
        : null;
      const layoutConfig = layeredPositions
        ? {
            ...getLayoutConfigs(orientation)[layout],
            ...overrides,
            positions: (node: { id(): string }) => layeredPositions.get(node.id()) ?? { x: 0, y: 0 },
          }
        : { ...getLayoutConfigs(orientation)[layout], ...overrides };

      // For non-animated layouts: hide container so no intermediate viewport state is painted.
      // The browser will only see the final fitted state when we restore visibility.
      const container = containerRef.current;
      if (!shouldAnimate && container) container.style.visibility = 'hidden';

      const layoutInstance = cy.layout(layoutConfig);
      layoutInstance.on('layoutstop', () => {
        fitGraph(cy);
        // Restore visibility after fit — browser paints final state only
        if (!shouldAnimate && container) container.style.visibility = '';
        // Defer unsuppression past the current paint frame to avoid racing with pending resize events
        requestAnimationFrame(() => { suppressResizeFitRef.current = false; });
      });
      layoutInstance.run();
    } else if (toAdd.length === 0 && removedCount === 0) {
      // Elements are the same — no layout needed, but unsuppress resize observer (deferred)
      requestAnimationFrame(() => { suppressResizeFitRef.current = false; });
    }
  }, [elements, layout]);

  // Update layout when layout type or orientation changes (NOT on initial mount —
  // the element update effect already runs the layout on mount).
  useEffect(() => {
    if (!layoutMountedRef.current) {
      layoutMountedRef.current = true;
      return;
    }
    const cy = cyRef.current;
    if (!cy || cy.elements().length === 0) return;

    // Layout-adaptive edge curve style
    const edgeStyle = getEdgeStyleForLayout(layout, orientation);
    cy.style().selector('edge').style(edgeStyle).update();

    const layoutInstance = cy.layout(getLayoutConfigs(orientation)[layout]);
    layoutInstance.on('layoutstop', () => {
      fitGraph(cy);
      // Density-based opacity: reduce for dense graphs
      if (cy.edges().length >= 50) {
        cy.edges().not('.critical-path').style('line-opacity', 0.25);
      }
    });
    layoutInstance.run();
  }, [layout, orientation]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
});
